/** Trendyol / pazaryeri webhook durumlarını kullanıcı dostu Türkçe aşama etiketlerine çevirir. */

const STAGE_LABELS: Record<string, string> = {
  created: 'sipariş alındı',
  awaiting: 'onay bekliyor',
  picking: 'hazırlanıyor',
  invoiced: 'faturalandı',
  shipped: 'kargolandı',
  delivered: 'teslim edildi',
  cancelled: 'iptal edildi',
  canceled: 'iptal edildi',
  cancel: 'iptal edildi',
  returned: 'iade edildi',
  unpacked: 'paketlenmedi',
  unsupplied: 'tedarik edilemedi',
  'un supplied': 'tedarik edilemedi',
  // Hepsiburada webhook durumları
  open: 'sipariş alındı',
  packaged: 'paketlendi',
  intransit: 'yolda',
  undelivered: 'teslim edilemedi',
  cancelledbycustomer: 'müşteri tarafından iptal edildi',
  claimawaitingaction: 'iade talebi bekliyor',
  claimaccepted: 'iade kabul edildi',
  claimrejected: 'iade reddedildi',
  claimcreated: 'iade oluşturuldu',
};

const PACKAGE_STATUS_LABELS: Record<string, string> = {
  created: 'Sipariş Oluşturuldu',
  picking: 'Sipariş Onaylandı',
  invoiced: 'Faturalandı',
  shipped: 'Sipariş Kargolandı',
  delivered: 'Sipariş Teslim Edildi',
  cancelled: 'Sipariş İptal Edildi',
  canceled: 'Sipariş İptal Edildi',
  returned: 'İade Edildi',
  unpacked: 'Paketlenmedi',
  unsupplied: 'Tedarik Edilemedi',
};

export const normalizeMarketplaceStatusKey = (status?: string): string =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

export const getMarketplaceStageLabel = (status?: string): string => {
  const key = normalizeMarketplaceStatusKey(status);
  if (!key) return 'güncellendi';
  return STAGE_LABELS[key] || key;
};

export const getPackageStatusDisplayLabel = (status?: string): string => {
  const key = normalizeMarketplaceStatusKey(status);
  if (!key) return status || 'Bilinmiyor';
  return PACKAGE_STATUS_LABELS[key] || status || 'Bilinmiyor';
};

export const buildOrderStatusToastMessage = (
  orderNumber: string,
  status?: string,
  options?: { isNew?: boolean; isCancelled?: boolean }
): string => {
  const safeNumber = orderNumber || '-';

  if (options?.isCancelled) {
    return `Siparişiniz: ${safeNumber} iptal edildi.`;
  }

  if (options?.isNew) {
    return `Siparişiniz: ${safeNumber} alındı.`;
  }

  const stage = getMarketplaceStageLabel(status);
  return `Siparişiniz: ${safeNumber} ${stage} aşamasına geçti.`;
};

export const formatMarketplaceDateTime = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === '') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Sipariş akışı sekmeleriyle aynı renk anahtarları */
export type OrderFlowColorKey =
  | 'received'
  | 'pending'
  | 'confirmed'
  | 'shipped'
  | 'delivered'
  | 'cancel_requested'
  | 'cancelled'
  | 'return_requests';

/** Sekme badge renkleriyle birebir uyumlu palet */
export const ORDER_FLOW_COLORS: Record<
  OrderFlowColorKey,
  { main: string; light: string; border: string }
> = {
  received: { main: '#2e7d32', light: '#e8f5e9', border: 'rgba(46, 125, 50, 0.35)' },
  pending: { main: '#ed6c02', light: '#fff3e0', border: 'rgba(237, 108, 2, 0.4)' },
  confirmed: { main: '#0288d1', light: '#e1f5fe', border: 'rgba(2, 136, 209, 0.35)' },
  shipped: { main: '#1565c0', light: '#e3f2fd', border: 'rgba(21, 101, 192, 0.35)' },
  delivered: { main: '#2e7d32', light: '#e8f5e9', border: 'rgba(46, 125, 50, 0.35)' },
  cancel_requested: { main: '#7b1fa2', light: '#f3e5f5', border: 'rgba(123, 31, 162, 0.35)' },
  cancelled: { main: '#d32f2f', light: '#ffebee', border: 'rgba(211, 47, 47, 0.4)' },
  return_requests: { main: '#ed6c02', light: '#fff3e0', border: 'rgba(237, 108, 2, 0.4)' },
};

const STATUS_TO_FLOW: Record<string, OrderFlowColorKey> = {
  created: 'received',
  awaiting: 'pending',
  'onay bekleyen': 'pending',
  'onay bekliyor': 'pending',
  pending: 'pending',
  picking: 'confirmed',
  invoiced: 'confirmed',
  packed: 'confirmed',
  preparing: 'confirmed',
  hazırlanıyor: 'confirmed',
  hazirlaniyor: 'confirmed',
  confirmed: 'confirmed',
  shipped: 'shipped',
  kargoda: 'shipped',
  kargolandı: 'shipped',
  kargolandi: 'shipped',
  delivered: 'delivered',
  'teslim edildi': 'delivered',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  cancel: 'cancelled',
  iptal: 'cancelled',
  'iptal edildi': 'cancelled',
  'cancel request': 'cancel_requested',
  'cancel requested': 'cancel_requested',
  'iptal talebi': 'cancel_requested',
  returned: 'return_requests',
  'iade edildi': 'return_requests',
  unpacked: 'cancelled',
  unsupplied: 'cancelled',
  'tedarik edilemedi': 'cancelled',
};

export const resolveMarketplaceStatusFlowColor = (
  status?: string,
  options?: { isNew?: boolean; isCancelled?: boolean }
): OrderFlowColorKey => {
  if (options?.isCancelled) return 'cancelled';
  if (options?.isNew) return 'received';

  const key = normalizeMarketplaceStatusKey(status);
  if (STATUS_TO_FLOW[key]) return STATUS_TO_FLOW[key];

  if (key.includes('iptal') && key.includes('talep')) return 'cancel_requested';
  if (key.includes('iptal') || key.includes('cancel')) return 'cancelled';
  if (key.includes('iade') || key.includes('return')) return 'return_requests';
  if (key.includes('teslim') || key.includes('deliver')) return 'delivered';
  if (key.includes('kargo') || key.includes('ship')) return 'shipped';
  if (key.includes('hazır') || key.includes('hazir') || key.includes('pick') || key.includes('invoice')) {
    return 'confirmed';
  }
  if (key.includes('onay') || key.includes('await') || key.includes('pending')) return 'pending';

  return 'received';
};
